import { TroveStatus } from "@mezo/sdk"
import { createSelector } from "@reduxjs/toolkit"

import { BorrowRepayStrategy } from "#/shared/types"

import { RootState } from "../reducers"

// Only show loading state on the first load
export const selectIsBorrowLoadingData = (state: RootState) =>
  state.borrow.isLoadingBorrowData

export const selectIsLoanFlow = (state: RootState) => state.borrow.isLoanFlow

export const selectLoanAmount = (state: RootState) => state.borrow.loanAmount

export const selectCollateralAmount = (state: RootState) =>
  state.borrow.collateralAmount

export const selectUpdateLoanAmount = (state: RootState) =>
  state.borrow.updateLoanAmount

export const selectRepayLoanAmount = (state: RootState) =>
  state.borrow.repayLoanAmount

export const selectUpdateStrategy = (state: RootState) =>
  state.borrow.updateStrategy

export const selectRepayStrategy = (state: RootState) =>
  state.borrow.repayStrategy

export const selectPartialRepayStrategy = (state: RootState) =>
  state.borrow.repayStrategy === BorrowRepayStrategy.PARTIAL
    ? state.borrow.partialRepaymentStrategy
    : null

export const selectUpdateCollateralStrategy = (state: RootState) =>
  state.borrow.updateCollateralStrategy

export const selectUpdateCollateralAmount = (state: RootState) =>
  state.borrow.updateCollateralAmount

export const selectUpdateCollateralRatio = (state: RootState) =>
  state.borrow.updateCollateralRatio

export const selectCollateralPrice = (state: RootState) =>
  state.borrow.collateralPrice

export const selectLiquityProps = (state: RootState) => state.borrow.liquity

export const selectGasCompensation = (state: RootState) =>
  state.borrow.liquity ? state.borrow.liquity.gasCompensation : null

export const selectIsRecoveryModeEnabled = (state: RootState) =>
  state.borrow.liquity ? state.borrow.liquity.isRecoveryModeEnabled : false

// In most cases use selectMinimumCollateralRatioInCurrentMode to get
// the correct value for the current mode (normal or recovery)
export const selectMinimumCollateralRatio = (state: RootState) =>
  state.borrow.liquity ? state.borrow.liquity.minimumCollateralRatio : null

export const selectCriticalCollateralRatio = (state: RootState) =>
  state.borrow.liquity ? state.borrow.liquity.criticalCollateralRatio : null

export const selectMinimumCollateralRatioInCurrentMode = createSelector(
  selectMinimumCollateralRatio,
  selectCriticalCollateralRatio,
  selectIsRecoveryModeEnabled,
  (minimumCollateralRatio, criticalCollateralRatio, isRecoveryModeEnabled) =>
    isRecoveryModeEnabled ? criticalCollateralRatio : minimumCollateralRatio,
)

export const selectBorrowingFeeRate = (state: RootState) =>
  state.borrow.liquity ? state.borrow.liquity.borrowingFeeRate : null

export const selectMinimumDebt = (state: RootState) =>
  state.borrow.liquity ? state.borrow.liquity.minimumDebt : null

export const selectMinimumNetDebt = (state: RootState) =>
  state.borrow.liquity ? state.borrow.liquity.minimumNetDebt : null

export const selectUserLoan = (state: RootState) => state.borrow.currentLoan

export const selectUserHasLoan = (state: RootState) =>
  state.borrow.currentLoan
    ? state.borrow.currentLoan.status === TroveStatus.ACTIVE
    : false

export const selectUserHasClosedOrLiquidatedLoan = (state: RootState) =>
  state.borrow.currentLoan
    ? state.borrow.currentLoan.status !== TroveStatus.ACTIVE &&
      state.borrow.currentLoan.status !== TroveStatus.NON_EXISTENT
    : false

export const selectClaimableMatsForBorrow = (state: RootState) =>
  state.borrow.matsForBorrow?.unclaimed ?? 0

export const selectTotalMatsForBorrow = (state: RootState) =>
  state.borrow.matsForBorrow?.total ?? 0

export const selectBorrowHistory = (state: RootState) =>
  state.borrow.borrowHistory
